import React, { useEffect, useRef, useState } from 'react';
import { useSkuEntitlements, can } from './PriceListEntitlements';
import { type FuseResult } from 'fuse.js';
import { loadSkuSearchIndex, searchSkuIndex } from '../../api/sku/sku-search-api';
import {
  searchSkuByPartNumber,
  getSkuDiscounts,
  createSkuPartsDiscount,
  createSkuDiscount,
  updateSkuDiscount,
  deleteSkuDiscount,
  getSkuPartDiscountsByIdentifier,
  getSkuPartsDiscountsByProductId,
  deleteSkuPartsDiscount,
} from '../../api/price-list/price-list-api';
import {
  type SkuDto,
  type SkuDiscountDto,
  type SkuPartsDiscountDto,
  type SearchIndexEntry,
  type IndexState,
  useBrands,
  fmtPercent,
  pctToInput,
  applyHighlight,
  groupDiscountsByCategory,
  DiscountTreeGroup,
  MIN_CHARS,
} from './PriceListShared';

// ─── AssignDiscountPanel ──────────────────────────────────────────

const AssignDiscountPanel: React.FC<{ catalog?: SkuDiscountDto[] | null }> = ({ catalog: catalogProp }) => {
  const entitlements = useSkuEntitlements();
  const [query, setQuery] = useState('');
  const [indexState, setIndexState] = useState<IndexState>('idle');
  const [indexError, setIndexError] = useState<string | null>(null);
  const fuseRef = useRef<Awaited<ReturnType<typeof loadSkuSearchIndex>> | null>(null);
  const [searchResults, setSearchResults] = useState<FuseResult<SearchIndexEntry>[]>([]);

  const [selectedPN, setSelectedPN] = useState<string | null>(null);
  const [sku, setSku] = useState<SkuDto | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [assignedDiscounts, setAssignedDiscounts] = useState<SkuDiscountDto[] | null>(null);
  const [partsDiscounts, setPartsDiscounts] = useState<SkuPartsDiscountDto[] | null>(null);

  const [showAssignTree, setShowAssignTree] = useState(false);
  const [discountCatalog, setDiscountCatalog] = useState<SkuDiscountDto[] | null>(null);
  const [selectedDiscountId, setSelectedDiscountId] = useState('');
  const [assignCondition, setAssignCondition] = useState('');
  const [assignIsDefault, setAssignIsDefault] = useState(false);
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assignExpiryDate, setAssignExpiryDate] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (fuseRef.current || indexState !== 'idle') return;
    setIndexState('loading');
    loadSkuSearchIndex()
      .then(idx => { fuseRef.current = idx; setIndexState('ready'); })
      .catch(() => { setIndexError('No se pudo cargar el índice de búsqueda'); setIndexState('error'); });
  }, [indexState]);

  useEffect(() => {
    if (!fuseRef.current || query.trim().length < MIN_CHARS) { setSearchResults([]); return; }
    const t = setTimeout(() => setSearchResults(searchSkuIndex(fuseRef.current!, query)), 180);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (catalogProp != null) setDiscountCatalog(catalogProp);
  }, [catalogProp]);

  useEffect(() => {
    if (!showAssignTree || discountCatalog || catalogProp != null) return;
    fetch('/data/sku/discount-categories.json')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: any[]) => setDiscountCatalog(data.map(d => ({
        discountId:   d.discountId   ?? d.DiscountId,
        category:     d.category     ?? d.Category,
        subcategory:  d.subcategory  ?? d.Subcategory,
        discountRate: d.discountRate ?? d.DiscountRate,
        startDate:    d.startDate    ?? d.StartDate    ?? null,
        expiryDate:   d.expiryDate   ?? d.ExpiryDate   ?? null,
      }))))
      .catch(() => {});
  }, [showAssignTree, discountCatalog, catalogProp]);

  const resetAssignFields = () => {
    setSelectedDiscountId('');
    setAssignCondition('');
    setAssignIsDefault(false);
    setAssignStartDate('');
    setAssignExpiryDate('');
    setAssignNotes('');
    setActionError(null);
  };

  const resetAssignForm = () => {
    setShowAssignTree(false);
    resetAssignFields();
  };

  const selectPN = (pn: string) => {
    setSelectedPN(pn);
    setQuery('');
    setSearchResults([]);
    setSku(null);
    setAssignedDiscounts(null);
    setPartsDiscounts(null);
    setLoadError(null);
    resetAssignForm();
  };

  const loadData = async () => {
    if (!selectedPN) return;
    setLoadingData(true);
    setLoadError(null);
    setSku(null);
    setAssignedDiscounts(null);
    setPartsDiscounts(null);
    resetAssignForm();

    let foundSku: SkuDto | null = null;
    let foundDiscounts: SkuDiscountDto[] = [];
    let err: string | null = null;

    await Promise.all([
      searchSkuByPartNumber(selectedPN, {
        onSuccess: (data) => { foundSku = data[0] ?? null; },
        onNotFound: () => { err = `No se encontró el SKU: ${selectedPN}`; },
        onBadRequest: (msg) => { err = msg; },
        onError: (msg) => { err = msg; },
      }),
      getSkuPartDiscountsByIdentifier(selectedPN, {
        onSuccess: (data) => { foundDiscounts = data; },
        onNotFound: () => { foundDiscounts = []; },
        onBadRequest: () => { foundDiscounts = []; },
        onError: () => { foundDiscounts = []; },
      }),
    ]);

    if (err) {
      setLoadError(err);
      setLoadingData(false);
      return;
    }

    // TypeScript doesn't track closure assignments — cast to reset narrowing
    const loadedSku = foundSku as SkuDto | null;
    setSku(loadedSku);
    setAssignedDiscounts(foundDiscounts);

    if (loadedSku != null && loadedSku.skuId != null) {
      await getSkuPartsDiscountsByProductId(Number(loadedSku.skuId), {
        onSuccess: (data) => setPartsDiscounts(data),
        onNotFound: () => setPartsDiscounts([]),
        onBadRequest: () => setPartsDiscounts([]),
        onError: () => setPartsDiscounts([]),
      });
    } else {
      setPartsDiscounts([]);
    }

    setLoadingData(false);
  };

  const handleAssign = async () => {
    if (!sku?.skuId || !selectedDiscountId) return;
    setSaving(true);
    setActionError(null);
    await createSkuPartsDiscount(
      {
        discountId: selectedDiscountId,
        productId: Number(sku.skuId),
        condition: assignCondition.trim() || null,
        isDefault: assignIsDefault || null,
        startDate: assignStartDate || null,
        expiryDate: assignExpiryDate || null,
        notes: assignNotes.trim() || null,
      },
      {
        onSuccess: (created) => {
          const catItem = discountCatalog?.find(d => d.discountId === selectedDiscountId) ?? null;
          if (catItem) setAssignedDiscounts(prev => prev ? [...prev, catItem] : [catItem]);
          setPartsDiscounts(prev => prev ? [...prev, created] : [created]);
          resetAssignForm();
        },
        onBadRequest: (msg) => setActionError(msg),
        onError: (msg) => setActionError(msg),
      }
    );
    setSaving(false);
  };

  const handleUnassign = async (partsDiscountId: number, discountId: string) => {
    if (!window.confirm('¿Desea desasignar este descuento?')) return;
    setSaving(true);
    setActionError(null);
    await deleteSkuPartsDiscount(partsDiscountId, {
      onSuccess: () => {
        setAssignedDiscounts(prev => prev ? prev.filter(d => d.discountId !== discountId) : null);
        setPartsDiscounts(prev => prev ? prev.filter(pd => Number(pd.id) !== partsDiscountId) : null);
      },
      onBadRequest: (msg) => setActionError(msg),
      onError: (msg) => setActionError(msg),
    });
    setSaving(false);
  };

  const discountGroups = discountCatalog ? groupDiscountsByCategory(discountCatalog) : null;
  const VISIBLE = 30;

  return (
    <div className="assign-panel">
      <div className="assign-panel-title">Asignar / Desasignar Descuentos</div>

      <div className="assign-search-wrap">
        <div className="bp-searchbar">
          <span className="bp-searchbar-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </span>
          <input
            className="bp-searchbar-input"
            type="text"
            placeholder="Número de parte o descripción…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            disabled={indexState !== 'ready' || selectedPN !== null}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button className="bp-searchbar-clear" onClick={() => setQuery('')} aria-label="Limpiar búsqueda">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>
        {indexState === 'loading' && (
          <div className="bp-index-loading"><span className="bp-spinner" aria-hidden="true" /><span>Cargando índice…</span></div>
        )}
        {indexError && <p className="pricelist-save-error">{indexError}</p>}
        {query.trim().length >= MIN_CHARS && searchResults.length === 0 && (
          <p className="bp-hint">Sin resultados</p>
        )}
      </div>

      {searchResults.length > 0 && (
        <div className="bp-results assign-search-results">
          {searchResults.slice(0, VISIBLE).map((result, i) => {
            const entry = result.item;
            const pnMatch = result.matches?.find(m => m.key === 'part_number');
            const descMatch = result.matches?.find(m => m.key === 'description');
            return (
              <div key={entry.part_number + i} className="bp-result-row">
                <div className="bp-result-body">
                  <div className="bp-result-top">
                    <span className="bp-result-pn">{applyHighlight(entry.part_number, pnMatch?.indices)}</span>
                    {entry.product_type && <span className="bp-result-tag">{entry.product_type}</span>}
                  </div>
                  {entry.description && (
                    <div className="bp-result-desc">{applyHighlight(entry.description, descMatch?.indices)}</div>
                  )}
                </div>
                <div className="bp-result-actions">
                  <button className="bp-prices-btn" onClick={() => selectPN(entry.part_number)}>
                    Seleccionar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedPN && (
        <div className="assign-selected-row">
          <div className="assign-selected-info">
            <span className="assign-pn-label">Número de parte</span>
            <span className="assign-pn-value">{selectedPN}</span>
            {sku?.description && <span className="assign-pn-desc">{sku.description}</span>}
          </div>
          <div className="assign-selected-actions">
            <button
              className="pricelist-cancel-btn"
              disabled={loadingData || saving}
              onClick={() => { setSelectedPN(null); setSku(null); setAssignedDiscounts(null); setPartsDiscounts(null); setLoadError(null); setActionError(null); }}
            >
              Cambiar
            </button>
            <button className="pricelist-save-btn" onClick={loadData} disabled={loadingData || saving}>
              {loadingData ? 'Buscando…' : 'Buscar descuentos'}
            </button>
          </div>
        </div>
      )}

      {loadError && <p className="pricelist-save-error">{loadError}</p>}

      {assignedDiscounts !== null && partsDiscounts !== null && (
        <div className="assign-results">
          <div className="assign-results-header">
            Descuentos asignados a <strong>{selectedPN}</strong>
          </div>

          {assignedDiscounts.length === 0 ? (
            <p className="pricelist-search-status">Sin descuentos asignados</p>
          ) : (
            <table className="pricelist-discount-table assign-discount-table">
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th>Subcategoría</th>
                  <th>Descuento</th>
                  <th>Criterio</th>
                  <th>Predet.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {assignedDiscounts.map((d, i) => {
                  const pd = partsDiscounts.find(p => p.discountId === d.discountId);
                  return (
                    <tr key={d.discountId ?? i}>
                      <td>{d.category ?? '—'}</td>
                      <td>{d.subcategory ?? '—'}</td>
                      <td>{fmtPercent(d.discountRate)}</td>
                      <td>{pd?.condition ?? '—'}</td>
                      <td>
                        {pd?.isDefault ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#27ae60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-label="Sí">
                            <path d="M20 6 9 17l-5-5"/>
                          </svg>
                        ) : '—'}
                      </td>
                      <td>
                        {can.unassignDiscount(entitlements) && (
                          <button
                            className="assign-unassign-btn"
                            disabled={saving || !pd}
                            onClick={() => pd ? handleUnassign(Number(pd.id), d.discountId ?? '') : undefined}
                          >
                            Desasignar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {can.assignDiscount(entitlements) && <div className="assign-tree-section">
            <button
              className={`assign-tree-toggle${showAssignTree ? ' assign-tree-toggle--open' : ''}`}
              onClick={() => { setShowAssignTree(o => !o); if (showAssignTree) resetAssignFields(); }}
              disabled={saving}
            >
              <span className="assign-tree-chevron">{showAssignTree ? '▾' : '▸'}</span>
              Asignar un nuevo descuento
            </button>

            {showAssignTree && (
              <div className="assign-tree-body">
                {!discountCatalog ? (
                  <p className="pricelist-search-status">Cargando descuentos…</p>
                ) : (
                  <>
                    <div className="pricelist-discount-tree">
                      {discountGroups && Array.from(discountGroups.entries()).map(([cat, items]) => (
                        <DiscountTreeGroup
                          key={cat}
                          category={cat}
                          items={items}
                          selectedId={selectedDiscountId}
                          onSelect={id => {
                            const next = selectedDiscountId === id ? '' : id;
                            setSelectedDiscountId(next);
                            const def = next ? discountCatalog?.find(d => d.discountId === next) : null;
                            setAssignStartDate(def?.startDate ? def.startDate.substring(0, 10) : '');
                            setAssignExpiryDate(def?.expiryDate ? def.expiryDate.substring(0, 10) : '');
                          }}
                          defaultOpen={items.some(item => item.discountId === selectedDiscountId)}
                        />
                      ))}
                    </div>

                    <div className="assign-extra-fields">
                      <div className="pricelist-edit-field">
                        <label className="pricelist-edit-label">Condición</label>
                        <input
                          className="pricelist-edit-input"
                          type="text"
                          value={assignCondition}
                          onChange={e => setAssignCondition(e.target.value)}
                          placeholder="Ej. Distribuidor autorizado"
                        />
                      </div>
                      <div className="pricelist-edit-field">
                        <label className="pricelist-edit-label">Fecha de inicio</label>
                        <input
                          className="pricelist-edit-input"
                          type="date"
                          value={assignStartDate}
                          onChange={e => setAssignStartDate(e.target.value)}
                        />
                      </div>
                      <div className="pricelist-edit-field">
                        <label className="pricelist-edit-label">Fecha de vencimiento</label>
                        <input
                          className="pricelist-edit-input"
                          type="date"
                          value={assignExpiryDate}
                          onChange={e => setAssignExpiryDate(e.target.value)}
                        />
                      </div>
                      <div className="pricelist-edit-field pricelist-edit-field--inline">
                        <label className="pricelist-edit-label">Descuento predeterminado</label>
                        <input
                          type="checkbox"
                          checked={assignIsDefault}
                          onChange={e => setAssignIsDefault(e.target.checked)}
                        />
                      </div>
                      <div className="pricelist-edit-field">
                        <label className="pricelist-edit-label">Notas</label>
                        <textarea
                          className="pricelist-edit-textarea"
                          rows={2}
                          value={assignNotes}
                          onChange={e => setAssignNotes(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="assign-tree-footer">
                      <div className="pricelist-discount-selected">
                        {(() => {
                          const sel = discountCatalog.find(d => d.discountId === selectedDiscountId);
                          return sel ? (
                            <>
                              {sel.category}
                              {sel.subcategory ? ` › ${sel.subcategory}` : ''}
                              <span className="pricelist-discount-selected-rate">&nbsp;{fmtPercent(sel.discountRate)}</span>
                            </>
                          ) : (
                            <span className="pricelist-discount-none">Sin selección</span>
                          );
                        })()}
                      </div>
                      <button
                        className="pricelist-save-btn"
                        disabled={saving || !selectedDiscountId}
                        onClick={handleAssign}
                      >
                        {saving ? 'Guardando…' : 'Asignar'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>}

          {actionError && <p className="pricelist-save-error">{actionError}</p>}
        </div>
      )}
    </div>
  );
};

// ─── DiscountGroup ────────────────────────────────────────────────

const DiscountGroup: React.FC<{
  category: string;
  items: SkuDiscountDto[];
  editingId: string;
  onEdit: (d: SkuDiscountDto) => void;
  onDelete: (discountId: string) => void;
}> = ({ category, items, editingId, onEdit, onDelete }) => {
  const entitlements = useSkuEntitlements();
  return (
  <div className="pricelist-discount-group">
    <div className="pricelist-discount-group-title">
      {category}
      <span className="dtab-group-count">{items.filter(d => d.discountId).length}</span>
    </div>
    <table className="pricelist-discount-table">
      <thead>
        <tr>
          <th>Subcategoría</th>
          <th>Marca</th>
          <th>Descuento</th>
          <th>Inicio</th>
          <th>Vencimiento</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((d, i) => (
          <tr key={d.discountId ?? i} className={editingId === d.discountId ? 'dtab-row--editing' : ''}>
            {(d.discountId == null || d.discountId === '') ? (
              <td colSpan={6} className="pricelist-no-discount">Sin descuento</td>
            ) : (
              <>
                <td>{d.subcategory ?? '—'}</td>
                <td>{d.brandName ?? '—'}</td>
                <td>{fmtPercent(d.discountRate)}</td>
                <td>{d.startDate ? d.startDate.substring(0, 10) : '—'}</td>
                <td>{d.expiryDate ? d.expiryDate.substring(0, 10) : '—'}</td>
                <td>
                  {can.updateDiscount(entitlements) && (
                    <div className="dtab-row-actions">
                      <button className="dtab-icon-btn" onClick={() => onEdit(d)} title="Editar">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button className="dtab-icon-btn dtab-icon-btn--delete" onClick={() => onDelete(d.discountId!)} title="Eliminar">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

// ─── DescuentosTab ────────────────────────────────────────────────

const DescuentosTab: React.FC = () => {
  const entitlements = useSkuEntitlements();
  const brands = useBrands();
  const [discounts, setDiscounts] = useState<SkuDiscountDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAssignPanel, setShowAssignPanel] = useState(false);

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadDiscounts = async () => {
    setLoading(true);
    setError(null);
    await getSkuDiscounts({
      onSuccess: (data) => setDiscounts(data),
      onNotFound: () => setError('No se encontraron descuentos'),
      onBadRequest: (msg) => setError(msg),
      onError: (msg) => setError(msg),
    });
    setLoading(false);
  };

  const openCreate = () => {
    setDraft({ category: '', subcategory: '', discountRate: '', brandId: '', startDate: '', expiryDate: '', notes: '' });
    setFormMode('create');
    setEditingId('');
    setSaveError(null);
  };

  const openEdit = (d: SkuDiscountDto) => {
    const matchedBrand = brands.find(b => b.Id === d.brandName || b.Name === d.brandName);
    setDraft({
      category: d.category ?? '',
      subcategory: d.subcategory ?? '',
      discountRate: pctToInput(d.discountRate),
      brandId: matchedBrand?.Id ?? '',
      startDate: d.startDate ? d.startDate.substring(0, 10) : '',
      expiryDate: d.expiryDate ? d.expiryDate.substring(0, 10) : '',
      notes: d.notes ?? '',
    });
    setFormMode('edit');
    setEditingId(d.discountId ?? '');
    setSaveError(null);
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingId('');
    setDraft({});
    setSaveError(null);
  };

  const setField = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDraft(d => ({ ...d, [key]: e.target.value }));

  const handleSave = async () => {
    if (!draft.category.trim()) { setSaveError('La categoría es obligatoria'); return; }
    setSaving(true);
    setSaveError(null);
    const selectedBrand = brands.find(b => b.Id === draft.brandId);
    const dto = {
      ...(formMode === 'edit' ? { discountId: editingId } : {}),
      category: draft.category.trim(),
      subcategory: draft.subcategory.trim() || null,
      discountRate: draft.discountRate !== '' ? Number(draft.discountRate) / 100 : null,
      brandId: draft.brandId || null,
      brandName: selectedBrand?.Name ?? null,
      startDate: draft.startDate || null,
      expiryDate: draft.expiryDate || null,
      notes: draft.notes?.trim() || null,
    } as SkuDiscountDto;

    if (formMode === 'create') {
      await createSkuDiscount(dto, {
        onSuccess: (created) => { setDiscounts(prev => prev ? [...prev, created] : [created]); closeForm(); },
        onBadRequest: (msg) => setSaveError(msg),
        onError: (msg) => setSaveError(msg),
      });
    } else {
      await updateSkuDiscount(editingId, dto, {
        onSuccess: () => {
          setDiscounts(prev => prev ? prev.map(d => d.discountId === editingId ? { ...d, ...dto } : d) : null);
          closeForm();
        },
        onBadRequest: (msg) => setSaveError(msg),
        onError: (msg) => setSaveError(msg),
      });
    }
    setSaving(false);
  };

  const handleDelete = async (discountId: string) => {
    if (!window.confirm('¿Desea eliminar este descuento?')) return;
    await deleteSkuDiscount(discountId, {
      onSuccess: () => setDiscounts(prev => prev ? prev.filter(d => d.discountId !== discountId) : null),
      onBadRequest: (msg) => setError(msg),
      onError: (msg) => setError(msg),
    });
  };

  const groups = discounts ? groupDiscountsByCategory(discounts) : null;

  return (
    <div>
      <div className="dtab-toolbar">
        <button
          className={`assign-toggle-btn${showAssignPanel ? ' assign-toggle-btn--active' : ''}`}
          onClick={() => setShowAssignPanel(s => !s)}
        >
          {showAssignPanel ? (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
              Cerrar asignación
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/>
              </svg>
              Asignacion de descuentos
            </>
          )}
        </button>
        <div className="dtab-toolbar-right">
          <button className="pricelist-load-btn" onClick={loadDiscounts} disabled={loading}>
            {loading ? 'Cargando…' : 'Cargar descuentos'}
          </button>
          {discounts !== null && can.addDiscount(entitlements) && (
            <button className="pricelist-save-btn" onClick={openCreate} disabled={formMode === 'create'}>
              + Nuevo Descuento
            </button>
          )}
        </div>
      </div>

      {showAssignPanel && <AssignDiscountPanel catalog={discounts} />}

      {formMode !== null && (
        <div className="dtab-form-panel">
          <div className="dtab-form-title">
            {formMode === 'create' ? 'Nuevo Descuento' : 'Editar Descuento'}
          </div>
          <div className="pricelist-edit-form">
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Categoría *</label>
              <input className="pricelist-edit-input" type="text"
                value={draft.category} onChange={setField('category')} autoFocus />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Subcategoría</label>
              <input className="pricelist-edit-input" type="text"
                value={draft.subcategory} onChange={setField('subcategory')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Descuento (%)</label>
              <input className="pricelist-edit-input" type="number"
                step="0.01" min="0" max="100"
                value={draft.discountRate} onChange={setField('discountRate')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Marca</label>
              <select className="pricelist-edit-select pricelist-edit-select--country"
                value={draft.brandId} onChange={setField('brandId')}>
                <option value="">— Sin marca —</option>
                {brands.map(b => (
                  <option key={b.Id} value={b.Id}>{b.Name}</option>
                ))}
              </select>
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Fecha de inicio</label>
              <input className="pricelist-edit-input" type="date"
                value={draft.startDate} onChange={setField('startDate')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Fecha de vencimiento</label>
              <input className="pricelist-edit-input" type="date"
                value={draft.expiryDate} onChange={setField('expiryDate')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Notas</label>
              <textarea className="pricelist-edit-textarea" rows={2}
                value={draft.notes ?? ''}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} />
            </div>
            {saveError && <p className="pricelist-save-error">{saveError}</p>}
            <div className="pricelist-edit-actions">
              <button className="pricelist-cancel-btn" onClick={closeForm} disabled={saving}>Cancelar</button>
              <button className="pricelist-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando…' : formMode === 'create' ? 'Crear' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <p className="pricelist-search-status">Cargando descuentos…</p>}
      {error && <p className="pricelist-error">{error}</p>}
      {discounts === null && !loading && !error && (
        <div className="dtab-empty-state">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="7" width="20" height="14" rx="2"/>
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
          </svg>
          <p className="dtab-empty-title">Descuentos no cargados</p>
          <p className="dtab-empty-desc">Haga clic en "Cargar descuentos" para ver la lista completa.</p>
        </div>
      )}
      {groups && (
        <div className="pricelist-discount-grid">
          {Array.from(groups.entries()).map(([cat, items]) => (
            <DiscountGroup
              key={cat}
              category={cat}
              items={items}
              editingId={editingId}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DescuentosTab;
